import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useStylePresetsQuery } from "@/lib/queries";
import type { StylePreset } from "@/lib/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { usePendingStyleStore, toast } from "@/stores/ui";

const CAT_LABELS: Record<string, string> = {
  all: "全部",
  realistic: "写实",
  illustration: "插画",
  concept: "概念",
  design: "设计",
};

export function StylesView() {
  const navigate = useNavigate();
  const setPendingStyle = usePendingStyleStore((s) => s.set);
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { items: styles } = useStylePresetsQuery();

  const categories = useMemo(() => {
    const set = new Set<string>(["all"]);
    styles.forEach((s) => s.category && set.add(s.category));
    return Array.from(set);
  }, [styles]);

  const visible = useMemo(
    () =>
      activeCategory === "all"
        ? styles
        : styles.filter((s) => s.category === activeCategory),
    [styles, activeCategory],
  );

  const selected = useMemo(
    () => styles.find((s) => s.style_preset_id === selectedId) ?? null,
    [styles, selectedId],
  );

  return (
    <div className="flex flex-col gap-5 px-6 py-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">风格广场</h3>
        <p className="mt-1 text-xs text-muted-foreground">
          点击风格查看大图，再选「使用此模板」回到自由创作。
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setActiveCategory(c)}
            className={cn(
              "rounded-full border px-4 py-1.5 text-sm font-medium transition-colors",
              c === activeCategory
                ? "border-accent bg-accent text-accent-foreground"
                : "border-border bg-card text-muted-foreground hover:border-border/80 hover:text-foreground",
            )}
          >
            {CAT_LABELS[c] ?? c}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
          暂无风格预设。
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3">
          {visible.map((s) => (
            <button
              key={s.style_preset_id}
              type="button"
              onClick={() => setSelectedId(s.style_preset_id)}
              className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card text-left transition-all hover:-translate-y-1 hover:border-border/80 hover:shadow-lg"
            >
              <div className="aspect-square overflow-hidden bg-muted">
                {s.preview_url ? (
                  <img
                    src={s.preview_url}
                    alt={s.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform group-hover:scale-[1.04]"
                    onError={(e) => {
                      const card = (e.currentTarget as HTMLElement).closest("button");
                      if (card) (card as HTMLElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div className="grid h-full place-items-center text-2xl font-bold text-muted-foreground">
                    {s.name.slice(0, 2)}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 px-3 py-2">
                <strong className="text-sm font-semibold text-foreground">
                  {s.name}
                </strong>
                <span className="inline-flex w-fit rounded-md bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {CAT_LABELS[s.category ?? ""] ?? s.category}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      <StyleDetailModal
        style={selected}
        onClose={() => setSelectedId(null)}
        onUse={(id) => {
          setSelectedId(null);
          setPendingStyle(id);
          toast(`已套用风格：${selected?.name ?? ""}`);
          navigate("/free");
        }}
      />
    </div>
  );
}

function StyleDetailModal({
  style,
  onClose,
  onUse,
}: {
  style: StylePreset | null;
  onClose: () => void;
  onUse: (styleId: string) => void;
}) {
  return (
    <Dialog open={!!style} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="grid max-h-[88vh] max-w-[min(960px,90vw)] grid-cols-1 gap-0 overflow-hidden p-0 md:grid-cols-[minmax(0,1.2fr)_minmax(280px,1fr)]">
        {style && (
          <>
            <div className="min-h-[280px] bg-muted md:min-h-[480px]">
              {style.preview_url && (
                <img
                  src={style.preview_url}
                  alt={style.name}
                  className="h-full w-full object-cover"
                />
              )}
            </div>
            <div className="flex flex-col gap-3 p-6">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {CAT_LABELS[style.category ?? ""] ?? style.category}
              </p>
              <DialogTitle className="text-2xl">{style.name}</DialogTitle>
              <DialogDescription>提示词后缀（自动拼接）：</DialogDescription>
              <pre className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-border bg-muted p-3 font-mono text-xs text-foreground/80">
                {style.prompt_suffix?.replace(/^,\s*/, "") || "（无后缀）"}
              </pre>
              <div className="mt-auto flex flex-col gap-2">
                <Button
                  size="lg"
                  className="w-full"
                  onClick={() => onUse(style.style_preset_id)}
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  使用此模板
                </Button>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  取消
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
